import { useCallback, useEffect, useState } from 'react'
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Tooltip,
  MenuItem,
  FormControlLabel,
  Switch
} from '@material-ui/core'
import { Check, Pencil, Plus, Trash2, X } from 'lucide-react'
import { toast } from 'react-toastify'

import toastError from '../../errors/toastError'
import api from '../../services/api'
import { i18n } from '../../translate/i18n'
import { ButtonSecondary } from '../ButtonSecondary'
import { InputFieldText } from '../InputFieldText'
import SelectInput from '../SelectInput'
import { useStyles } from './style'

const isValidEmail = email => /^\S+@\S+\.\S+$/.test(email)

const ConnectionAlertModal = ({ open, onClose, whatsAppId }) => {
  const classes = useStyles()
  const [emails, setEmails] = useState([])
  const [interval, setInterval] = useState(60)
  const [enabled, setEnabled] = useState(true)
  const [newEmail, setNewEmail] = useState('')
  const [editingId, setEditingId] = useState(null)
  const [editingValue, setEditingValue] = useState('')

  const loadData = useCallback(async () => {
    try {
      const [{ data: config }, { data: emailsList }] = await Promise.all([
        api.get(`/whatsapp/${whatsAppId}/connection-alert`),
        api.get(`/whatsapp/${whatsAppId}/connection-alert/emails`)
      ])
      setInterval(config.disconnectAlertInterval || 60)
      setEnabled(config.disconnectAlertEnabled !== false)
      setEmails(emailsList || [])
    } catch (err) {
      toastError(err)
    }
  }, [whatsAppId])

  useEffect(() => {
    if (!open || !whatsAppId) return
    loadData()
  }, [open, whatsAppId, loadData])

  const handleIntervalChange = async e => {
    const value = Number(e.target.value)
    setInterval(value)
    try {
      await api.put(`/whatsapp/${whatsAppId}/connection-alert`, {
        disconnectAlertInterval: value,
        disconnectAlertEnabled: enabled
      })
      toast.success(i18n.t('connectionAlertModal.intervalSaved'))
    } catch (err) {
      toastError(err)
    }
  }

  const handleEnabledChange = async e => {
    const value = e.target.checked
    setEnabled(value)
    try {
      await api.put(`/whatsapp/${whatsAppId}/connection-alert`, {
        disconnectAlertInterval: interval,
        disconnectAlertEnabled: value
      })
      toast.success(
        value
          ? i18n.t('connectionAlertModal.enabled')
          : i18n.t('connectionAlertModal.disabled')
      )
    } catch (err) {
      toastError(err)
    }
  }

  const handleAddEmail = async () => {
    if (!isValidEmail(newEmail)) {
      toast.error(i18n.t('connectionAlertModal.invalidEmail'))
      return
    }
    try {
      await api.post(`/whatsapp/${whatsAppId}/connection-alert/emails`, {
        email: newEmail
      })
      setNewEmail('')
      toast.success(i18n.t('connectionAlertModal.emailAdded'))
      loadData()
    } catch (err) {
      toastError(err)
    }
  }

  const handleStartEdit = email => {
    setEditingId(email.id)
    setEditingValue(email.email)
  }

  const handleCancelEdit = () => {
    setEditingId(null)
    setEditingValue('')
  }

  const handleSaveEdit = async email => {
    if (!isValidEmail(editingValue)) {
      toast.error(i18n.t('connectionAlertModal.invalidEmail'))
      return
    }
    try {
      await api.put(
        `/whatsapp/${whatsAppId}/connection-alert/emails/${email.id}`,
        { email: editingValue }
      )
      setEditingId(null)
      setEditingValue('')
      toast.success(i18n.t('connectionAlertModal.emailUpdated'))
      loadData()
    } catch (err) {
      toastError(err)
    }
  }

  const handleDelete = async email => {
    if (!window.confirm(i18n.t('connectionAlertModal.confirmDelete'))) return
    try {
      await api.delete(
        `/whatsapp/${whatsAppId}/connection-alert/emails/${email.id}`
      )
      toast.success(i18n.t('connectionAlertModal.emailDeleted'))
      loadData()
    } catch (err) {
      toastError(err)
    }
  }

  const handleClose = () => {
    setEditingId(null)
    setEditingValue('')
    setNewEmail('')
    onClose()
  }

  const handleKeyDown = e => {
    if (e.key === 'Enter') {
      handleAddEmail()
    }
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      scroll="paper"
    >
      <DialogTitle className={classes.dialogTitle}>
        {i18n.t('connectionAlertModal.title')}
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={enabled}
                  onChange={handleEnabledChange}
                  color="primary"
                />
              }
              label={i18n.t('connectionAlertModal.enabledLabel')}
            />
          </Grid>
          <Grid item xs={12}>
            <SelectInput
              label={i18n.t('connectionAlertModal.intervalLabel')}
              value={interval}
              onChange={handleIntervalChange}
              disabled={!enabled}
              fullWidth
            >
              <MenuItem value={30}>
                {i18n.t('connectionAlertModal.intervals.30s')}
              </MenuItem>
              <MenuItem value={60}>
                {i18n.t('connectionAlertModal.intervals.1m')}
              </MenuItem>
              <MenuItem value={120}>
                {i18n.t('connectionAlertModal.intervals.2m')}
              </MenuItem>
            </SelectInput>
          </Grid>
          <Grid item xs={12}>
            <div className={`${classes.sectionTitle} ${!enabled ? classes.disabledSection : ''}`}>
              {i18n.t('connectionAlertModal.emailsTitle')}
            </div>
          </Grid>
          {emails.map(email => (
            <Grid item xs={12} key={email.id}>
              <div className={classes.emailRow}>
                {editingId === email.id ? (
                  <InputFieldText
                    value={editingValue}
                    onChange={e => setEditingValue(e.target.value)}
                    fullWidth
                    size="small"
                  />
                ) : (
                  <span className={classes.emailText}>{email.email}</span>
                )}
                <div className={classes.emailActions}>
                  {editingId === email.id ? (
                    <>
                      <Tooltip
                        title={i18n.t('connectionAlertModal.save')}
                        arrow
                      >
                        <IconButton
                          size="small"
                          onClick={() => handleSaveEdit(email)}
                        >
                          <Check size={18} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip
                        title={i18n.t('connectionAlertModal.cancel')}
                        arrow
                      >
                        <IconButton size="small" onClick={handleCancelEdit}>
                          <X size={18} />
                        </IconButton>
                      </Tooltip>
                    </>
                  ) : (
                    <>
                      <Tooltip
                        title={i18n.t('connectionAlertModal.edit')}
                        arrow
                      >
                        <IconButton
                          size="small"
                          onClick={() => handleStartEdit(email)}
                          disabled={!enabled}
                        >
                          <Pencil size={18} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip
                        title={i18n.t('connectionAlertModal.delete')}
                        arrow
                      >
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(email)}
                          disabled={!enabled}
                        >
                          <Trash2 size={18} />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                </div>
              </div>
            </Grid>
          ))}
          <Grid item xs={12}>
            <div className={classes.addRow}>
              <InputFieldText
                label={i18n.t('connectionAlertModal.newEmailLabel')}
                value={newEmail}
                onChange={e => setNewEmail(e.target.value)}
                onKeyDown={handleKeyDown}
                fullWidth
                size="small"
                disabled={!enabled}
              />
              <Tooltip title={i18n.t('connectionAlertModal.add')} arrow>
                <IconButton
                  size="small"
                  onClick={handleAddEmail}
                  className={classes.addButton}
                  disabled={!enabled}
                >
                  <Plus size={20} />
                </IconButton>
              </Tooltip>
            </div>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <ButtonSecondary
          onClick={handleClose}
          label={i18n.t('connectionAlertModal.close')}
        />
      </DialogActions>
    </Dialog>
  )
}

export default ConnectionAlertModal
